<template>
  <main class="main main--has-floating-buttons">
    <portal to="headerTitle">
      Connect.CX
    </portal>
    <template v-if="config !== null">
      <card :title="$t('admin.interfaces.pageChannelManagerCx.card.general')" cols="12 6@desktop" rows="5">
        <hm-card-inner>
          <hm-input-toggle :label="$t('admin.interfaces.pageChannelManagerCx.enabled')"
                           v-model="config.enabled"
                           :enabled-title="$t('enabled')"
                           :disabled-title="$t('disabled')"
                           :disabled="!canEnable"
                           cols="12 6@hd start-1"
                           name="enabled" />
        </hm-card-inner>
        <hm-card-inner v-if="config.enabled && (isEmpty(config.webhookIds) || isEmpty(config.webhookIds.booking))" :full-size="true">
          <hm-col cols="12">
            <span class="error-text error-text--full-size error-text--no-margin col-12">Webhooks have not been connected yet. Therefore reservations will <b>NOT</b> be imported, unless pulled manually.</span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner v-if="!config.enabled" :full-size="true">
          <hm-col cols="12">
            <span class="warning-text warning-text--full-size warning-text--no-margin col-12">Interface is <b><u>DISABLED</u></b>. Reservations will <b><u>NOT</u></b> be imported. Availability changes will <b><u>NOT</u></b> be synced. Price changes will <b><u>NOT</u></b> be synced.</span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner>
          <hm-input :label="$t('admin.interfaces.pageChannelManagerCx.name')"
                    v-model="config.name"
                    cols="12 6@hd start-1"
                    name="name" />
          <!-- isDefault -->
          <hm-input-toggle v-if="hasRole('ROLE_SYSADMIN')"
                           :label="$t('admin.interfaces.pageChannelManagerCx.isDefault')"
                           :value="config.isDefault"
                           :disabled="true"
                           :enabled-title="$t('admin.interfaces.pageChannelManagerCx.isDefaultEnabled')"
                           :disabled-title="$t('admin.interfaces.pageChannelManagerCx.isDefaultDisabled')"
                           cols="12 6@hd start-1"
                           name="isDefault" />
          <hm-input-toggle v-if="hasRole('ROLE_SYSADMIN')"
                           :label="$t('admin.interfaces.pageChannelManagerCx.manualMappingMode')"
                           v-model="config.manualMappingMode"
                           help="Group & Property only (rooms and rates need to be created through API)"
                           cols="12 6@hd start-1"
                           name="manualMappingMode" />
          <hm-multiselect id="channels"
                          v-model="config.channels"
                          :options="enums.CxChannel.map(it => ({ id: it, name: it}))"
                          :label="`${$t('admin.interfaces.pageChannelManagerCx.channels')} 🔒`"
                          :disabled="!(hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') || (hasRole('ROLE_SYSADMIN') && !['Rainmaker', 'Digibooking'].includes(hotelSettings.hotel.whiteLabel)))"
                          label-expression="${option.name}"
                          cols="12 6@hd start-1"
                          name="channels"/>
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.interfaces.pageChannelManagerCx.card.tenant')">
          <hm-input-toggle v-if="!config.manualMappingMode || hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                           :label="$t('admin.interfaces.pageChannelManagerCx.chainCreated')"
                           v-model="config.chainCreated"
                           :disabled="true"
                           cols="12 6@hd start-1"
                           name="chainCreated" />
          <hm-input :label="$t('admin.interfaces.pageChannelManagerCx.chainId')"
                    v-model="config.chainId"
                    :disabled="!(config.manualMappingMode && hasRole('ROLE_SYSADMIN'))"
                    cols="12 6@hd start-1"
                    name="chainId" />
          <hm-input v-if="!config.manualMappingMode"
                    :label="$t('admin.interfaces.pageChannelManagerCx.chainName')"
                    :value="hotelSettings.hotel.hotelName"
                    :disabled="true"
                    cols="12 6@hd start-1"
                    name="chainName" />
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.interfaces.pageChannelManagerCx.card.hotel')">
          <hm-input-toggle v-if="!config.manualMappingMode || hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                           :label="$t('admin.interfaces.pageChannelManagerCx.created')"
                           v-model="config.created"
                           :disabled="true"
                           cols="12 6@hd start-1"
                           name="created" />

          <hm-input :label="$t('admin.interfaces.pageChannelManagerCx.hotelId')"
                    v-model="config.hotelId"
                    :disabled="!(config.manualMappingMode && hasRole('ROLE_SYSADMIN'))"
                    cols="12 6@hd start-1"
                    name="hotelId" />
          <hm-input-select v-if="!config.manualMappingMode"
                           :label="$t('admin.interfaces.pageChannelManagerCx.hotelType')"
                           v-model="config.hotelType"
                           :disabled="config.created && !hasRole('ROLE_SYSADMIN')"
                           enum-name="ChannexHotelType"
                           cols="12 6@hd start-1"
                           name="hotelType" />
          <hm-textarea :label="$t('admin.interfaces.pageChannelManagerCx.hotelDescription')"
                       v-model="config.hotelDescription"
                       :input-rows="8"
                       cols="12 6@hd start-1"
                       name="hotelDescription"
          />
          <hm-button class="btn--blue"
                     cols="12 6@hd start-1"
                     icon-left="magic-wand"
                     @click="clickGenerateDescription">{{ $t('admin.interfaces.pageChannelManagerCx.actionGenerateDescription') }}</hm-button>
        </hm-card-inner>
        <hm-card-inner>
          <hm-multiselect id="excludedRoomTypes"
                          v-model="config.excludedRoomTypes"
                          :options="hotelSettings.roomTypes.all(config.excludedRoomTypes)"
                          :label="$t('admin.interfaces.pageChannelManagerCx.excludedRoomTypes')"
                          :label-expression="`\${option.name.${hotelSettings.details.primaryLanguage}}`"
                          cols="12 6@hd start-1"
                          name="excludedRoomTypes"/>
          <hm-multiselect id="excludedRatePlans"
                          v-model="config.excludedRatePlans"
                          :options="hotelSettings.ratePlans.all(config.excludedRatePlans).filter(it => it.ratePlanType === 'DayRate')"
                          :label="$t('admin.interfaces.pageChannelManagerCx.excludedRatePlans')"
                          :label-expression="`\${option.name.${hotelSettings.details.primaryLanguage}}`"
                          cols="12 6@hd start-1"
                          name="excludedRatePlans" />
          <hm-multiselect v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                          id="netRatePlans"
                          v-model="config.netRatePlans"
                          :options="hotelSettings.ratePlans.all(config.netRatePlans).filter(it => it.ratePlanType === 'DayRate')"
                          :label="`${$t('admin.interfaces.pageChannelManagerCx.netRatePlans')} 🔒`"
                          :help="$t('admin.interfaces.pageChannelManagerCx.netRatePlans_help')"
                          :label-expression="`\${option.name.${hotelSettings.details.primaryLanguage}}`"
                          cols="12 6@hd start-1"
                          name="netRatePlans" />
        </hm-card-inner>
        <hm-card-inner>
          <hm-input-select v-model="config.infantAcId"
                           :label="$t('admin.interfaces.pageChannelManagerCx.infantAcId')"
                           name="infantAcId"
                           cols="12 6@hd start-1">
            <option v-for="item in hotelSettings.ageCategories.all(config.infantAcId)" v-if="item.ageQualifier === 'Adult'" :value="item.id" v-html="`${item.name[$shortLocale]}`"></option>
            <option v-for="item in hotelSettings.ageCategories.all(config.infantAcId)" v-if="item.ageQualifier !== 'Adult' && !item.deleted" :value="item.id" v-html="`${item.name[$shortLocale]} (${item.ageFrom} - ${item.ageUntil})`"></option>
          </hm-input-select>
          <hm-input-select v-model="config.childAcId"
                           :label="$t('admin.interfaces.pageChannelManagerCx.childAcId')"
                           name="childAcId"
                           cols="12 6@hd start-1">
            <option v-for="item in hotelSettings.ageCategories.all(config.childAcId)" v-if="item.ageQualifier === 'Adult'" :value="item.id" v-html="`${item.name[$shortLocale]}`"></option>
            <option v-for="item in hotelSettings.ageCategories.all(config.childAcId)" v-if="item.ageQualifier !== 'Adult' && !item.deleted" :value="item.id" v-html="`${item.name[$shortLocale]} (${item.ageFrom} - ${item.ageUntil})`"></option>
          </hm-input-select>
          <hm-input-select v-model="config.defaultRatePlanId"
                           :label="$t('admin.interfaces.pageChannelManagerCx.defaultRatePlanId')"
                           name="defaultRatePlanId"
                           cols="12 6@hd start-1">
            <option v-for="item in hotelSettings.ratePlans.all(config.defaultRatePlanId)" v-if="item.ratePlanType === 'DayRate' && !item.deleted" :value="item.id" v-html="`${item.name[$shortLocale]}`"></option>
          </hm-input-select>
          <hm-input-select v-model="config.defaultRoomTypeId"
                           :label="$t('admin.interfaces.pageChannelManagerCx.defaultRoomTypeId')"
                           name="defaultRoomTypeId"
                           cols="12 6@hd start-1">
            <option v-for="item in hotelSettings.roomTypes.all(config.defaultRoomTypeId)" v-if="!item.deleted" :value="item.id" v-html="`${item.name[$shortLocale]}`"></option>
          </hm-input-select>
        </hm-card-inner>
      </card>
      <card title="Reservation Retrieval" cols="12 6@desktop">
        <hm-card-inner title="Retrieval Settings">
          <hm-input-toggle v-model="config.smartMatchHotelSpider"
                           label="Smart-Match existing Hotel-Spider reservations?"
                           help="Use with caution, there is a potential risk of matching wrong reservations."
                           cols="12 6@hd start-1"
                           name="smartMatchHotelSpider" />
          <hm-input-toggle v-model="config.smartMatchSalesbox"
                           label="Smart-Match existing Salesbox reservations?"
                           help="Use with caution, there is a potential risk of matching wrong reservations."
                           cols="12 6@hd start-1"
                           name="smartMatchSalesbox" />
        </hm-card-inner>
        <hm-card-inner title="Retrieve Reservation">
          <hm-input v-model="bookingId" label="Booking ID" cols="12" />
          <hm-button :disabled="isEmpty(bookingId)"
                     class="btn--orange"
                     icon-left="download"
                     @click="retrieveReservation">
            Retrieve Reservation <template v-if="!isEmpty(bookingId)">({{ bookingId }})</template>
          </hm-button>
        </hm-card-inner>
      </card>
      <card title="Default Addon Mapping" cols="12 6@desktop">
        <hm-card-inner full-size>
          <hm-col cols="12">
            <span class="info-text info-text--full-size info-text--no-margin col-12">
              Only services configured "per room per stay" can be selected.<br/><br/>
              <i>We do recommend using a service with the highest VAT in your country as a fallback, to prevent being accused of VAT fraud, in case you forget to change the service type, before creating the invoice</i>
            </span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner>
          <hm-input-select v-model="config.defaultIhs"
                           label="Default Service"
                           help="Is being used if no other service mapping matches."
                           cols="12 6@hd">
            <option v-for="item in hotelSettings.inHouseServices.all(config.defaultIhs).filter(it => it.id === config.defaultIhs || (it.rateFrequency === 'PerStay' && it.rateMode === 'PerRoom' && it.type !== 'PrePayment'))"
                    :disabled="item.rateFrequency !== 'PerStay' || item.rateMode !== 'PerRoom'"
                    :value="item.id"
                    v-html="item.name[$shortLocale]"></option>
            <template slot="icon-inline-right" slot-scope="slot">
              <input-icon-button v-tooltip="{ title: 'Auto-Create default mapping iHS', delay: 0, placement: 'right', positionFixed: true, trigger: 'hover', touch: false }"
                                 :border="true"
                                 :disabled="!isEmpty(config.defaultIhs)"
                                 icon="plus"
                                 @click="createDefaultIhsMapping" />
            </template>
          </hm-input-select>
        </hm-card-inner>
      </card>
      <card title="Addons" cols="12 6@desktop" rows="3">
        <hm-card-inner :full-size="true" title="Addons">
          <hm-table :hover="false"
                    columns="2"
                    grid-template-columns="1fr 1fr">
            <template slot="table-head">
              <div class="hm-table__th">Name</div>
              <div class="hm-table__th">Service</div>
            </template>
            <template v-for="(it, itIdx) in config.addonMapping">
              <div class="hm-table__td">
                <hm-input :inline="true"
                          :id="`addon-mapping-label-${itIdx}`"
                          v-model="it.name"
                          label="Name"
                          name="name" />
              </div>
              <div class="hm-table__td">
                <hm-input-select v-model="it.ihsId"
                                 :id="`ihsId-${itIdx}`"
                                 :inline="true"
                                 label="Service"
                                 cols="12 6@hd">
                  <option :value="null"></option>
                  <option v-for="item in hotelSettings.inHouseServices.all(it.ihsId).filter(it => it.id === it.ihsId || it.type !== 'PrePayment')"
                          :value="item.id"
                          v-html="item.name[$shortLocale]"></option>
                </hm-input-select>
              </div>
            </template>
          </hm-table>
        </hm-card-inner>
      </card>
      <card title="Policies" cols="12 6@desktop">
        <hm-card-inner title="General">
          <hm-input-toggle :label="$t('admin.interfaces.pageChannelManagerCx.adultsOnly')"
                           v-model="config.adultsOnly"
                           cols="12 6@hd start-1"
                           name="adultsOnly" />
          <!-- SMOKING -->
          <hm-input-select :label="$t('admin.interfaces.pageChannelManagerCx.smokingPolicy')"
                           v-model="config.smokingPolicy"
                           enum-name="CxSmokingPolicy"
                           cols="12 6@hd start-1"
                           name="smokingPolicy" />
        </hm-card-inner>
        <hm-card-inner title="Internet">
          <!-- INTERNET -->
          <hm-input-select :label="$t('admin.interfaces.pageChannelManagerCx.internetAccessType')"
                           v-model="config.internetAccessType"
                           enum-name="CxInternetAccessType"
                           cols="12 6@hd start-1"
                           name="internetAccessType" />
          <hm-input-select :label="$t('admin.interfaces.pageChannelManagerCx.internetAccessCoverage')"
                           v-model="config.internetAccessCoverage"
                           enum-name="CxInternetAccessCoverage"
                           cols="12 6@hd start-1"
                           name="internetAccessCoverage" />
          <hm-input-money v-model="config.internetAccessCost"
                          :currency="hotelSettings.details.currency"
                          :label="$t('admin.interfaces.pageChannelManagerCx.internetAccessCost')"
                          name="internetAccessCost"
                          cols="12 6@hd start-1" />
        </hm-card-inner>
        <hm-card-inner title="Parking">
          <!-- PARKING -->
          <hm-input-select :label="$t('admin.interfaces.pageChannelManagerCx.parkingType')"
                           v-model="config.parkingType"
                           enum-name="CxParkingType"
                           cols="12 6@hd start-1"
                           name="parkingType" />
          <hm-input-select v-if="config.parkingType !== 'None'"
                           :label="$t('admin.interfaces.pageChannelManagerCx.parkingReservationType')"
                           v-model="config.parkingReservationType"
                           enum-name="CxParkingReservationType"
                           cols="12 6@hd start-1"
                           name="parkingReservationType" />
          <hm-input-toggle v-if="config.parkingType !== 'None'"
                           :label="$t('admin.interfaces.pageChannelManagerCx.parkingIsPrivate')"
                           v-model="config.parkingIsPrivate"
                           cols="12 6@hd start-1"
                           name="parkingIsPrivate" />
          <hm-input-money v-if="config.parkingType !== 'None'"
                          v-model="config.parkingFee"
                          :currency="hotelSettings.details.currency"
                          :label="$t('admin.interfaces.pageChannelManagerCx.parkingFee')"
                          name="parkingFee"
                          cols="12 6@hd start-1" />
        </hm-card-inner>
        <hm-card-inner title="Pets">
          <!-- PETS -->
          <hm-input-select :label="$t('admin.interfaces.pageChannelManagerCx.petPolicy')"
                           v-model="config.petPolicy"
                           enum-name="CxPetPolicy"
                           cols="12 6@hd start-1"
                           name="petPolicy" />
          <hm-input-money v-if="config.petPolicy !== 'NotAllowed'"
                          v-model="config.petFee"
                          :currency="hotelSettings.details.currency"
                          :label="$t('admin.interfaces.pageChannelManagerCx.petFee')"
                          name="petFee"
                          cols="12 6@hd start-1" />
          <hm-input-money v-if="config.petPolicy !== 'NotAllowed'"
                          v-model="config.petRefundableDeposit"
                          :currency="hotelSettings.details.currency"
                          :label="$t('admin.interfaces.pageChannelManagerCx.petRefundableDeposit')"
                          name="petRefundableDeposit"
                          cols="12 6@hd start-1" />
        </hm-card-inner>
      </card>
      <card v-if="!isEmpty(propertyFacilities)" title="Facilities" cols="12" card-type="collapsible">
        <hm-card-inner title="Filter">
          <hm-input-toggle v-model="enabledFacilitiesOnly"
                           label="Selected facilities only?"
                           name="enabledFacilitiesOnly"
                           cols="12 3@hd 4@desktop 6@tablet" />
        </hm-card-inner>
        <hm-card-inner title="Facilities">
          <template v-for="facility in propertyFacilities">
            <hm-input-toggle v-if="!enabledFacilitiesOnly || (enabledFacilitiesOnly && config.facilities[facility.first])"
                             :label="facility.second"
                             v-model="config.facilities[facility.first]"
                             :key="facility.first"
                             :name="facility.second"
                             cols="12 3@hd 4@desktop 6@tablet" />
          </template>
        </hm-card-inner>
      </card>
      <card title="Room Type Codes" cols="12" card-type="collapsible" collapsed>
        <hm-card-inner :full-size="true">
          <hm-table :hover="true"
                    min-width="400px"
                    columns="2"
                    grid-template-columns="2fr 1fr">
            <template v-for="(cxRoomTypeId, roomTypeId) in config.roomTypes">
              <div class="hm-table__td">{{ roomTypeMap[roomTypeId].name[hotelSettings.details.primaryLanguage] }}</div>
              <div class="hm-table__td" @click="$copy(cxRoomTypeId)">{{ cxRoomTypeId }}</div>
            </template>
          </hm-table>
        </hm-card-inner>
      </card>
      <card title="Rate Plan Codes" cols="12" card-type="collapsible" collapsed>
        <hm-card-inner :full-size="true">
          <hm-table :hover="true"
                    min-width="400px"
                    columns="2"
                    grid-template-columns="2fr 1fr">
            <template v-for="product in config.ratePlans">
              <div class="hm-table__td">{{ ratePlanMap[product[0].first].name[hotelSettings.details.primaryLanguage] }} / {{ roomTypeMap[product[0].second].name[hotelSettings.details.primaryLanguage] }}</div>
              <div class="hm-table__td" @click="$copy(product[1])">{{ product[1] }}</div>
            </template>
          </hm-table>
        </hm-card-inner>
      </card>
      <card title="Webhooks" cols="12" card-type="collapsible">
        <hm-card-inner :full-size="true">
          <hm-table :hover="true"
                    min-width="400px"
                    columns="2"
                    grid-template-columns="2fr 1fr">
            <template v-for="(value, key) in config.webhookIds">
              <div class="hm-table__td">{{ key }}</div>
              <div class="hm-table__td" @click="$copy(value)">{{ value }}</div>
            </template>
          </hm-table>
        </hm-card-inner>
      </card>
    </template>

    <ai-text-generator />

    <hm-floating-buttons v-if="!isNil(config)">
      <hm-floating-button label="Show more" icon="fill-more" color="gray">
        <hm-floating-child-button v-if="config.created && hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                                  :disabled="isEmpty(config.defaultIhs)"
                                  :throttle="60000"
                                  label="Retrieve ALL Future Reservations 🔒"
                                  icon="download"
                                  color="mint"
                                  @click="showConfirmModal(
                                    'Are you sure, you would like to import ALL future reservations? Once started, you can leave this page, the process will continue in the background.',
                                    () => performAction('futureReservations'),
                                    'Continuing with this operation might result in duplicate reservations.')" />
        <hm-floating-child-button :throttle="10000"
                                  label="Delete Instance"
                                  icon="delete"
                                  color="red"
                                  @click="deleteInstance()" />
        <hm-floating-child-button v-if="isEmpty(config.hotelId)"
                                  :throttle="10000"
                                  label="Create new Chain"
                                  icon="upload"
                                  color="orange"
                                  @click="performAction('chain')" />
        <hm-floating-child-button v-if="config.chainCreated"
                                  :throttle="10000"
                                  label="Create/Update Instance"
                                  icon="upload"
                                  color="orange"
                                  @click="performAction('createInstance')" />
        <hm-floating-child-button v-if="config.chainCreated"
                                  :throttle="10000"
                                  label="Subscribe Webhooks"
                                  icon="upload"
                                  color="orange"
                                  @click="performAction('subscribe')" />
        <hm-floating-child-button v-if="config.created"
                                  :throttle="3500"
                                  label="Create/Update Room Types"
                                  icon="upload"
                                  color="orange"
                                  @click="performAction('roomTypes')" />
        <hm-floating-child-button v-if="config.created"
                                  :throttle="3500"
                                  :disabled="isEmpty(roomTypeMap)"
                                  label="Create/Update Rate Plans"
                                  icon="upload"
                                  color="orange"
                                  @click="performAction('ratePlans')" />
        <hm-floating-child-button v-if="config.created && hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                                  :throttle="3500"
                                  :disabled="isEmpty(roomTypeMap)"
                                  label="Create/Update Rate Plans (Force) 🔒"
                                  icon="upload"
                                  color="orange"
                                  @click="performAction('ratePlans', { force: true })" />
        <hm-floating-child-button v-if="config.created"
                                  :throttle="3500"
                                  label="Create/Update Cancellation Policies"
                                  icon="upload"
                                  color="orange"
                                  @click="performAction('updateCancellationPolicies')" />
        <hm-floating-child-button v-if="config.created"
                                  :throttle="3500"
                                  label="Create/Update Taxes/Fees"
                                  icon="upload"
                                  color="orange"
                                  @click="performAction('taxesFees')" />
        <!--        <hm-floating-child-button v-if="config.created"-->
        <!--                                  :throttle="3500"-->
        <!--                                  label="Clean-Up"-->
        <!--                                  icon="upload"-->
        <!--                                  color="orange"-->
        <!--                                  @click="performAction('cleanup')" />-->
        <!--        <hm-floating-child-button v-if="config.created"-->
        <!--                                  :throttle="3500"-->
        <!--                                  label="Update Google Usage"-->
        <!--                                  icon="download"-->
        <!--                                  color="orange"-->
        <!--                                  @click="performAction('updateGoogleChannelConnection')" />-->
        <hm-floating-child-button v-if="config.created"
                                  :throttle="3500"
                                  label="Update Rates & Availabilities"
                                  icon="upload"
                                  color="orange"
                                  @click="performAction('updateRatesAndAvailabilities')" />
        <hm-floating-child-button v-if="config.created"
                                  :disabled="isEmpty(config.defaultIhs)"
                                  :throttle="3500"
                                  label="Retrieve Reservations"
                                  icon="download"
                                  color="mint"
                                  @click="performAction('reservations')" />
      </hm-floating-button>
    </hm-floating-buttons>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, first, groupBy, indexOf, isEmpty, isNil, keyBy, maxBy, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import Card from '../../../lib/layout/Card';
import HmInputCheckbox from '../../../lib/inputs/HmInputCheckbox';
import HmInputMoney from '../../../lib/inputs/HmInputMoney';
import HmCardInner from '../../../lib/layout/HmCardInner';
import UpdateHotelSettingsMixin from '../../../../mixins/UpdateHotelSettingsMixin';
import HmInputSelect from '../../../lib/inputs/HmInputSelect';
import HmTextarea from '../../../lib/inputs/HmTextarea.vue';
import AiTextGenerator from '../../../lib/util/AiTextGenerator.vue';
import HmInputToggle from '../../../lib/inputs/HmInputToggle.vue';

export default {
  name: 'PageChannelManagerCx',
  components: { HmInputToggle, AiTextGenerator, HmTextarea, HmInputSelect, HmCardInner, HmInputMoney, HmInputCheckbox, Card },
  mixins: [HotelmeisterMixin, SaveWatchMixin, UpdateHotelSettingsMixin],

  autosave: [
    {
      name: 'config',
      variable: 'config',
      fn: function (cbSaveComplete) {
        const config = cloneDeep(this.config);

        config.addonMapping = Object.fromEntries(config.addonMapping.filter(it => !isEmpty(it.name) && !isEmpty(it.ihsId)).map(it => [it.name, it.ihsId]));
        config.facilities = Object.entries(config.facilities).filter(it => it[1] === true).map(it => it[0]);

        this.axios.put(this.serviceUrls.interfaces.channelManagerCx.get(this, this.config.id), config)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  data() {
    return {
      config: null,
      bookingId: null,
      propertyFacilities: null,
      enabledFacilitiesOnly: true,
    };
  },

  computed: {
    enums: function () {
      return this.$store.getters['enums/enums'];
    },
    roomTypeMap: function () {
      return keyBy(this.hotelSettings.roomTypes, 'id');
    },
    ratePlanMap: function () {
      return keyBy(this.hotelSettings.ratePlans, 'id');
    },
    canEnable() {
      if (isNil(this.config)) return false;
      if (this.config.created !== true) return false;
      if (isEmpty(this.config.defaultIhs)) return false;
      if (isEmpty(this.config.infantAcId)) return false;
      if (isEmpty(this.config.childAcId)) return false;
      if (isEmpty(this.config.defaultRatePlanId)) return false;
      if (isEmpty(this.config.defaultRoomTypeId)) return false;

      return true;
    },
  },

  watch: {
    'config.addonMapping': {
      handler: function () {
        this.autoAddNewRows(
          this.config.addonMapping,
          {
            name: null,
            ihsId: null,
          },
          it => isEmpty(it.name) && isEmpty(it.ihsId),
        );
      },
      deep: true,
    },
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    isEmpty,
    autoAddNewRows(newVal, emptyRecord, isRecordEmpty = it => isNil(it)) {
      if (isNil(newVal)) return;

      if (newVal.filter(it => isRecordEmpty(it)).length === 0) {
        newVal.push(emptyRecord);
      } else if (newVal.filter(it => isRecordEmpty(it)).length > 1) {
        const toDelete = first(newVal.filter(it => isRecordEmpty(it)));
        newVal.splice(indexOf(newVal, toDelete), 1);
      }
    },
    performAction(action, params = {}) {
      if (action === 'create' && !this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') && !['Hotelmeister', 'AllInOne'].includes(this.hotelSettings.hotel.whiteLabel)) {
        this.showErrorMessage('Access Denied. Please contact support.');
        return;
      }
      this.axios.post(this.serviceUrls.interfaces.channelManagerCx[action](this, this.config.id), null, { params: (params || {}) })
        .then((response) => {
          this.showSuccessMessage();
          this.showMessages(response);
          this.loadData();
          this.$serviceBus.$emit('hotelSettings.update.immediate');
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
          this.loadData();
        });
    },
    loadData() {
      this.axios.get(this.serviceUrls.interfaces.channelManagerCx.get(this, this.$route.params.configId))
        .then((response) => {
          const tmp = response.data.data;
          tmp.addonMapping = Object.entries(tmp.addonMapping).map(it => ({ name: it[0], ihsId: it[1] }));
          if (isNil(tmp.facilities)) tmp.facilities = [];
          if (isEmpty(tmp.facilities)) {
            tmp.facilities.push('b2a5d032-538b-4326-be39-94ef81c1b585'); // Toilet
          }
          tmp.facilities = Object.fromEntries(tmp.facilities.map(it => [it, true]));
          this.config = tmp;
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });

      this.axios.get(this.serviceUrls.interfaces.channelManagerCx.propertyFacilities(this, this.$route.params.configId))
        .then((response) => {
          this.propertyFacilities = response.data.data;
        })
        .catch((error) => {
          this.propertyFacilities = null;
        });
    },
    deleteInstance(force = false) {
      if (force) {
        this.axios.delete(this.serviceUrls.interfaces.channelManagerCx.deleteInstance(this, this.config.id))
          .then((response) => {
            this.showSuccessMessage();
            this.showMessages(response);
            this.loadData();
            this.$serviceBus.$emit('hotelSettings.update.immediate');
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
          });
      } else {
        this.showConfirmModal(
          this.$t('admin.interfaces.pageChannelManagerCx.confirmDelete', [this.hotelSettings.hotel.hotelName]),
          () => this.deleteInstance(true),
          this.$t('admin.interfaces.pageChannelManagerCx.confirmDeleteWarning'),
        );
      }
    },
    retrieveReservation() {
      this.axios.post(this.serviceUrls.interfaces.channelManagerCx.reservation(this, this.config.id, this.bookingId))
        .then((response) => {
          this.showSuccessMessage();
          this.showMessages(response);
          this.bookingId = null;
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });
    },
    clickGenerateDescription() {
      this.$serviceBus.$emit('aiTextGenerator.show', this.serviceUrls.aiTextGenerator.propertyDescription(this));
    },
    createDefaultIhsMapping() {
      const today = this.$moment().format('YYYYMMDD');
      const isValidToday = function (dateRanges) {
        return dateRanges.some((dateRange) => {
          if ((dateRanges.validFrom || '19700101') > today) return false;
          else if ((dateRanges.validTo || '20991231') < today) return false;
          else return true;
        });
      };

      const validVats = this.hotelSettings.taxes.all()
        .filter(it => it.taxType === 'VAT')
        .filter(it => isEmpty(it.vavalidityDateRanges) || isValidToday(it.validityDateRanges));

      const maxVat = maxBy(validVats, 'amount');

      if (isNil(maxVat)) {
        this.showErrorMessage('No valid VAT found');
      } else {
        this.axios.post(this.serviceUrls.inHouseService.list(this), {
          name: {
            [this.hotelSettings.details.primaryLanguage]: (this.hotelSettings.details.primaryLanguage === 'de' ? 'Sonstige OTA Zusatzleistung' : 'Other OTA additional service'),
          },
          description: {
            [this.hotelSettings.details.primaryLanguage]: '',
          },
          type: 'Others',
          inHouseServiceMode: 'Regular',
          applicableTaxes: [maxVat.id],
          ageCategories: [],
          level: 'RatePlan',
          rateMode: 'PerRoom',
          rateFrequency: 'PerStay',
          deleted: false,
        })
          .then((response) => {
            this.$serviceBus.$emit('hotelSettings.update.immediate');
            this.config.defaultIhs = response.data.data.id;
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
          });
      }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/interfaces/PageChannelManagerCx.vue